import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Lock, Plus, Trash2 } from "lucide-react";
import { Button, Dialog, Input } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useCreateEntries, useUpdateEntry, useVoidEntry } from "@/hooks/useFinancial";
import { activeEntries, monthBounds, parseMoney } from "@/lib/financial/calc";
import type { FinancialAccount, FinancialCategory, FinancialEntry } from "@/lib/financial/types";
import { cn } from "@/lib/utils";
import { isEntryLinked, isMonthLocked } from "./helpers";
import { useFinancialFormat } from "./useFinancialFormat";

export interface DrillTarget {
  category: FinancialCategory;
  year: number;
  month: number;
}

interface Props {
  target: DrillTarget | null;
  entries: FinancialEntry[];
  accounts: FinancialAccount[];
  today: string;
  canEdit: boolean;
  onClose: () => void;
}

function Row({ entry, locked, onRemove }: { entry: FinancialEntry; locked: boolean; onRemove: () => void }) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const update = useUpdateEntry();
  const [date, setDate] = useState(entry.competence_date);
  const [desc, setDesc] = useState(entry.description);
  const [value, setValue] = useState(fmt.moneyInput(Number(entry.amount)));
  const negative = Number(entry.amount) < 0;

  const save = async (patch: Partial<{ competence_date: string; description: string; amount: number; occurred_at: string }>) => {
    try {
      await update.mutateAsync({ id: entry.id, patch });
    } catch {
      toast({ title: t("toasts.error"), variant: "destructive" });
    }
  };

  return (
    <div className={cn("grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[140px_minmax(0,1fr)_150px_36px] sm:items-center", locked && "pointer-events-none opacity-55")}>
      <Input
        type="date"
        aria-label={t("drill.date")}
        value={date}
        disabled={locked || isEntryLinked(entry)}
        onChange={(e) => setDate(e.target.value)}
        onBlur={() => date && date !== entry.competence_date && save({ competence_date: date })}
        className="col-span-2 sm:col-span-1"
      />
      <Input
        aria-label={t("drill.description")}
        value={desc}
        disabled={locked || isEntryLinked(entry)}
        onChange={(e) => setDesc(e.target.value)}
        onBlur={() => desc !== entry.description && save({ description: desc })}
        className="col-span-2 sm:col-span-1"
      />
      <div className="flex items-center gap-1.5 rounded-lg border border-input bg-background px-2.5">
        <span className="whitespace-nowrap text-xs text-muted-foreground">{negative ? "−" : "+"} R$</span>
        <input
          aria-label={t("drill.value")}
          inputMode="decimal"
          value={value}
          disabled={locked || isEntryLinked(entry)}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            const v = Math.abs(parseMoney(value));
            if (v > 0 && v !== Math.abs(Number(entry.amount))) save({ amount: negative ? -v : v });
          }}
          className="h-9 w-full bg-transparent text-right text-sm font-semibold tabular-nums outline-none"
        />
      </div>
      <Button size="icon-sm" variant="outline" aria-label={t("drill.remove")} disabled={locked || isEntryLinked(entry)} onClick={onRemove}>
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </div>
  );
}

export function DrillDialog({ target, entries, accounts, today, canEdit, onClose }: Props) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const [unlocked, setUnlocked] = useState(false);
  const create = useCreateEntries();
  const voidEntry = useVoidEntry();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ date: "", desc: "", value: "" });

  if (!target) return null;
  const { from, to } = monthBounds(target.year, target.month);
  const rows = activeEntries(entries)
    .filter((e) => e.category_id === target.category.id && e.competence_date >= from && e.competence_date <= to)
    .sort((a, b) => a.competence_date.localeCompare(b.competence_date));
  const total = rows.reduce((a, e) => a + Number(e.amount), 0);
  const locked = !canEdit || (isMonthLocked(target.year, target.month, today) && !unlocked);
  const showLock = canEdit && isMonthLocked(target.year, target.month, today) && !unlocked;

  const startAdd = () => {
    setDraft({ date: `${from.slice(0, 8)}15`, desc: fmt.category(target.category), value: "" });
    setAdding(true);
  };
  const saveDraft = async () => {
    const account = accounts.find((a) => a.kind === "bank") ?? accounts[0];
    const v = Math.abs(parseMoney(draft.value));
    if (!account || !v || !draft.date) {
      toast({ title: t("form.amountInvalid"), variant: "destructive" });
      return;
    }
    try {
      await create.mutateAsync([
        {
          account_id: account.id,
          category_id: target.category.id,
          amount: target.category.direction === "out" ? -v : v,
          description: draft.desc.trim() || fmt.category(target.category),
          occurred_at: new Date(`${draft.date}T12:00:00`).toISOString(),
          competence_date: draft.date,
        },
      ]);
      setAdding(false);
    } catch {
      toast({ title: t("toasts.error"), variant: "destructive" });
    }
  };

  const remove = async (id: string) => {
    try {
      await voidEntry.mutateAsync(id);
      toast({ title: t("toasts.deleted") });
    } catch {
      toast({ title: t("toasts.error"), variant: "destructive" });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => { if (!o) { setUnlocked(false); onClose(); } }}
      title={t("drill.title", { category: fmt.category(target.category), month: fmt.monthLong(target.month), year: target.year })}
      closeLabel={t("drill.done")}
      className="sm:max-w-[680px]"
      footer={
        <div className="flex w-full items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">{t("drill.total")}</div>
            {/* no sentido da conta: despesa sem sinal, mas receita que ficou negativa (estornos) mostra o "−" */}
            <div className="font-display text-xl font-extrabold tracking-tight">{fmt.brl(target.category.direction === "out" ? -total : total)}</div>
          </div>
          <Button onClick={() => { setUnlocked(false); onClose(); }}>{t("drill.done")}</Button>
        </div>
      }
    >
      {showLock ? (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-state-warning/50 bg-state-warning-soft p-3.5 text-state-warning-foreground sm:flex-row sm:items-start">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p className="flex-1 text-sm">{t("drill.locked")}</p>
          <Button size="sm" variant="outline" onClick={() => setUnlocked(true)}>{t("drill.unlock")}</Button>
        </div>
      ) : null}
      <div className="flex flex-col gap-2">
        {rows.map((e) => <Row key={e.id} entry={e} locked={locked} onRemove={() => remove(e.id)} />)}
        {!rows.length ? <p className="text-sm text-muted-foreground">{t("drill.empty")}</p> : null}
      </div>
      {!locked && adding ? (
        <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[140px_minmax(0,1fr)_150px_36px] sm:items-center">
          <Input type="date" aria-label={t("drill.date")} value={draft.date} min={from} max={to} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} className="col-span-2 sm:col-span-1" />
          <Input aria-label={t("drill.description")} value={draft.desc} onChange={(e) => setDraft((d) => ({ ...d, desc: e.target.value }))} className="col-span-2 sm:col-span-1" />
          <Input aria-label={t("drill.value")} inputMode="decimal" placeholder="0,00" autoFocus value={draft.value} onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))} className="text-right" />
          <Button size="icon-sm" aria-label={t("drill.save")} onClick={saveDraft} loading={create.isPending}>
            <Check className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      ) : null}
      {!locked && !adding ? (
        <Button variant="outline" size="sm" className="mt-3 border-dashed" onClick={startAdd}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          {t("drill.add")}
        </Button>
      ) : null}
    </Dialog>
  );
}
